import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, throwError, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ICoreState } from '../store/core.reducer';
import { RefreshToken, Logout } from '../store/session/session.actions';
import { CORE_API_ROUTES } from '../configs/api-routes';
import { CORE_CONFIG_TOKEN, ICoreConfig } from '../configs/core.config';
import { Navigate } from '@ngxs/router-plugin';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  activatedPath: string;

  constructor(
    private store: Store,
    @Inject(CORE_API_ROUTES) private apis,
    @Inject(CORE_CONFIG_TOKEN) private coreConfig: ICoreConfig,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url === this.coreConfig.API_BASE + this.apis.auth.refreshToken) {
      return this.handleRefreshTokenRequest(req, next);
    }
    // Get the auth token from the service.
    const originalToken = this.store.selectSnapshot<string>(({ core }: { core: ICoreState }) => {
      return core.session ? core.session.token : null;
    });
    if (originalToken) {
      const authReq = this.addHeaderToken(req, originalToken);
      // send cloned request with header to the next handler.
      return next.handle(authReq).pipe(
        catchError((err: HttpErrorResponse) => {
          // token expired
          if (err.status === 401 && err.error.resultCode === 10030) {
            return this.store.dispatch(new RefreshToken(originalToken)).pipe(
              catchError(() => this.store.dispatch([new Logout(), new Navigate(['/login'])])),
              switchMap(() => {
                const newToken = this.store.selectSnapshot<string>(
                  ({
                    core: {
                      session: { token },
                    },
                  }: {
                    core: ICoreState;
                  }) => token,
                );
                return of(this.addHeaderToken(req, newToken));
              }),
              switchMap(refreshedAuthReq => {
                return next.handle(refreshedAuthReq);
              }),
            );
          } else {
            return throwError(err);
          }
        }),
      );
    }
    return next.handle(req);
  }

  private handleRefreshTokenRequest(req: HttpRequest<any>, next: HttpHandler) {
    const refresh_token = this.store.selectSnapshot<string>(({ core }: { core: ICoreState }) => {
      return core.session ? core.session.refresh_token : null;
    });
    const authReq = this.addHeaderToken(req, refresh_token);
    return next.handle(authReq).pipe(
      catchError((err: HttpErrorResponse) => {
        return throwError(err);
      }),
    );
  }

  addHeaderToken(req, token): HttpRequest<any> {
    return req.clone({
      headers: req.headers.set('Authorization', `token ${token}`),
    });
  }
}
